/* eslint-disable camelcase */
import {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import {
  useNavigate,
  useLocation,
} from 'react-router';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';

import {
  decodeParam,
} from 'web-utils';

import {
  uiActions,
} from 'web-actions';
import {
  useCompleteTutorial,
} from 'web-hooks'

import {
  Spinner,
  NotFound,
} from 'web-components';

import {
  Flex,
  SVG,
} from 'web-styled';

import Messages from './Messages';
import Footer from './Footer';
import * as S from './styled';

interface Props {
  id?: string
};

const Chat = ({ id }: Props) => {
  const navigate = useNavigate();
  const mapState = useCallback(state => ({
    pro: coreSelectors.getClientPro(state),
    user: coreSelectors.getProfile(state),
    readStatus: getStatus(
      state,
      [
        'messages.requests.fetchMessages.status',
      ],
    ),
    messages: coreSelectors.getMessages(
      id,
    )(state),
    ...getUiState([
      'openedTooltips',
    ])(state),
    conversations: coreSelectors.getConversations(state),
  }), [id]);

  const {
    pro,
    user,
    messages,
    readStatus,
    conversations,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const location = useLocation();
  const { completeTutorialStep } = useCompleteTutorial()
  const params = decodeParam(location.search);
  const isPro = user.is_pro;
  const conversation = conversations.find(
    c => c.id === Number(id),
  );

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(
        coreActions.requestMessages({
          id,
          qs: {
            page: 1,
            per_page: 50,
          },
        }),
      );
    } else if (!isPro && conversations.length) {
      const newUrl = `/messages/${conversations[0].id}`;
      navigate(newUrl);
    } else if (!params.recipient) {
      navigate('/messages');
    }
  }, [id]);

  const handleSubmit = (values: any, actions: any) => {
    dispatch(
      uiActions.requestSendMessage({
        payload: {
          ...values,
          recipient_id: isPro ? (params.recipient || conversation?.recipient?.user_id) : pro?.id,
          message_category: '1',
        },
        conversationId: id,
        callbacks: {
          ...actions,
          onSuccess: (result: any) => {
            actions.onSuccess(result);
            completeTutorialStep('send_message')

            if (id === 'new' && result?.conversation_id) {
              navigate(`/messages/${result.conversation_id}`);
            }
          },
        },
      }),
    );
  };

  const handleDelete = () => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: Number(id),
      resourceType: 'conversations',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete messages',
          bodyText: 'Are you sure you want to delete this conversation?',
          confirmText: 'Delete',
        },
        onSuccess: () => {
          navigate('/messages');
        },
        onConfirmAction,
      }),
    );
  };

  const onMarkRead = () => {
    if (conversation?.has_unread_messages) {
      const ids = messages
        .filter(
          message => !message.is_read,
        )
        .map(message => message.id);
      dispatch(
        coreActions.requestMarkReadMessages({
          ids,
          conversationId: id!,
        }),
      );
    }
  };

  return (
    <S.Chat>
      <S.Header>
        {(id === 'new' || !isPro) ? (
          <div />
        ) : (
          <Flex
            width="44px"
            height="44px"
            align="center"
            justify="center"
            clickable
            onClick={handleDelete}
          >
            <SVG.TrashAlt size={18} className="danger-color" />
          </Flex>
        )}
      </S.Header>
      {!readStatus.pending && !readStatus.failed && (
        <S.Body>
          <Messages
            user={user}
            messages={messages}
            onMarkRead={onMarkRead}
          />
        </S.Body>
      )}

      {readStatus.pending && (
        <Spinner />
      )}

      {readStatus.failed && (
        <NotFound title="Error" />
      )}
      <Footer
        showTooltip={id === 'new'}
        onSubmit={handleSubmit}
      />
    </S.Chat>
  );
};

export default Chat;
